import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import PageTitle from '../components/coreLayout/PageTitle';
import Seo from '../components/coreLayout/Seo';

interface ComparisonRow {
    readonly aspect: string;
    readonly heli: string;
    readonly fixed: string;
}

const comparisonRows: readonly ComparisonRow[] = [
    {
        aspect: 'Price',
        heli: 'Typically far more expensive to run — and to book',
        fixed: 'Much better value for the same time over the reef',
    },
    {
        aspect: 'Window seat',
        heli: 'Larger helicopters often have middle seats',
        fixed: 'Every passenger is guaranteed a window seat',
    },
    {
        aspect: 'Landing in remote spots',
        heli: 'Can land almost anywhere',
        fixed: 'Takes off and lands at the airport',
    },
    {
        aspect: 'Ride comfort over the reef',
        heli: 'Smoother in genuinely rough air',
        fixed: 'Just as smooth — the reef has no turbulence or thermals',
    },
    {
        aspect: 'Reef views & holiday photos',
        heli: 'Open cabin, great for specialist work',
        fixed: 'High wing — you look straight down at the reef',
    },
    {
        aspect: 'Professional doors-off filming',
        heli: 'Purpose-built for it',
        fixed: 'Not designed for specialist filming',
    },
];

const points: readonly { readonly heading: string; readonly body: string }[] = [
    {
        heading: 'Price',
        body: 'Helicopters are expensive aircraft to operate, and that cost shows up in the ticket. For the same time in the air over the Great Barrier Reef, a fixed-wing scenic flight usually costs significantly less.',
    },
    {
        heading: 'Landing flexibility',
        body: "Being able to land almost anywhere is a helicopter's signature trick — perfect for reaching a remote sandbar or beach. But on a non-stop scenic flight, where you take off and land at the same airport, that flexibility simply isn't a factor.",
    },
    {
        heading: 'Window seats',
        body: 'Most larger helicopters have middle seats, so a window isn’t guaranteed for everyone on board. Our high-wing aircraft are laid out so every passenger gets a guaranteed window seat — which is the whole point of a scenic flight.',
    },
    {
        heading: 'Ride comfort',
        body: 'It’s true a helicopter can feel smoother when the air is genuinely rough. But the airspace over the Great Barrier Reef is unusually stable: there are no hills or ridgelines to stir up turbulence, and because the water sits at a near-uniform temperature there are none of the thermals you’d get over land. In practice, you won’t notice a smoother ride in a helicopter out over the reef.',
    },
    {
        heading: 'Photos & video',
        body: 'Helicopters are fantastic for specialist jobs like doors-off filming. For a standard scenic flight, though, the photo and video opportunities are very similar. Our wing sits above the cabin, so you’re looking down past it onto the reef — the strut and wing can get in the way of professional-style aerial shots, but not your view of the water below or the experience of flying over it.',
    },
];

const heliSx = {
    container: {
        paddingLeft: { xs: '14px', sm: '24px' },
        paddingRight: { xs: '14px', sm: '24px' },
        paddingBottom: { xs: 6, sm: 8 },
    },
    intro: {
        fontSize: { xs: 16, sm: 18 },
        lineHeight: 1.7,
        opacity: 0.85,
        maxWidth: 760,
        mb: 1.5,
    },
    disclosure: {
        fontSize: { xs: 14, sm: 15 },
        lineHeight: 1.6,
        opacity: 0.7,
        fontStyle: 'italic',
        maxWidth: 760,
        mb: 4,
    },
    sectionHeading: {
        fontSize: { xs: 22, sm: 28 },
        fontWeight: 600,
        color: 'primary.main',
        mt: { xs: 5, sm: 6 },
        mb: 2,
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 2,
        overflow: 'hidden',
    },
    th: {
        textAlign: 'left',
        fontWeight: 600,
        fontSize: { xs: 13, sm: 15 },
        padding: { xs: '10px 10px', sm: '14px 16px' },
        backgroundColor: 'rgba(0,0,0,0.03)',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
    },
    thFixed: {
        color: 'primary.main',
    },
    td: {
        fontSize: { xs: 13, sm: 15 },
        lineHeight: 1.5,
        padding: { xs: '10px 10px', sm: '14px 16px' },
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        verticalAlign: 'top',
    },
    tdAspect: {
        fontWeight: 600,
        whiteSpace: { sm: 'nowrap' },
    },
    tdFixed: {
        backgroundColor: 'rgba(33, 150, 243, 0.05)',
    },
    pointHeading: {
        fontSize: { xs: 17, sm: 19 },
        fontWeight: 600,
        mb: 0.75,
    },
    pointBody: {
        fontSize: { xs: 15, sm: 16 },
        lineHeight: 1.65,
        opacity: 0.82,
        mb: 3,
        maxWidth: 800,
    },
    chooseCard: {
        flex: 1,
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 3,
        p: { xs: 2.5, sm: 3 },
    },
    chooseCardFixed: {
        borderColor: 'primary.main',
        backgroundColor: 'rgba(33, 150, 243, 0.04)',
    },
    chooseHeading: {
        fontSize: { xs: 17, sm: 19 },
        fontWeight: 600,
        mb: 1.5,
    },
    chooseItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        fontSize: { xs: 14.5, sm: 15.5 },
        lineHeight: 1.5,
        mb: 1,
    },
    ctaWrap: {
        textAlign: 'center',
        mt: { xs: 6, sm: 8 },
    },
    ctaButton: {
        px: 4,
        py: 1.25,
        fontSize: { xs: 15, sm: 16 },
        fontWeight: 600,
        borderRadius: 8,
        textTransform: 'none',
    },
} as const;

const heliChoose = [
    'Price is no object',
    'You want to land somewhere remote',
    "You're doing professional filming or photography",
];
const fixedChoose = [
    'You want a guaranteed window seat',
    'You’re after a classic scenic flight over the reef',
    'You want the best value for money',
];

const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'Are there helicopter tours in Cairns?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, helicopter tours operate in Cairns, but for a non-stop scenic flight over the Great Barrier Reef a fixed-wing scenic flight offers a guaranteed window seat, an equally smooth ride and much better value. GSL Aviation flies high-wing fixed-wing aircraft daily from Cairns Airport.',
            },
        },
        {
            '@type': 'Question',
            name: 'Helicopter or fixed-wing for a Great Barrier Reef scenic flight?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Choose a helicopter if you need to land in a remote spot or are doing professional doors-off filming. For a regular scenic flight over the reef, fixed-wing wins on value and gives every passenger a guaranteed window seat, with a ride that is just as smooth over the calm reef airspace.',
            },
        },
        {
            '@type': 'Question',
            name: 'Do you get a window seat on a fixed-wing scenic flight?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. GSL Aviation’s high-wing aircraft are configured so every passenger has a guaranteed window seat, and the high wing means you look straight down onto the reef.',
            },
        },
    ],
};

export default function HelicopterTours() {
    return (
        <>
            <Seo
                title="Helicopter Tours Cairns vs Scenic Flights — A Real Comparison | GSL Aviation"
                description="Thinking about a helicopter tour in Cairns? Here's how helicopter and fixed-wing scenic flights over the Great Barrier Reef really compare — window seats, price, photos and ride comfort."
                path="/cairns/helicopter-tours"
                jsonLd={faqJsonLd}
            />
            <Container maxWidth="lg" sx={heliSx.container}>
                <PageTitle
                    title="Helicopter Tours in Cairns"
                    tagline="Helicopter vs fixed-wing scenic flights — how they really compare"
                    compact
                />

                <Typography sx={heliSx.intro}>
                    Searching for a helicopter tour in Cairns? You have two great ways to see the Great Barrier Reef
                    from above — by helicopter, or by fixed-wing scenic flight. Both are unforgettable. Here&apos;s a
                    straight look at how they compare so you can choose the right one for your trip.
                </Typography>
                <Typography sx={heliSx.disclosure}>
                    Full disclosure: we fly fixed-wing aircraft. But for a typical scenic flight over the reef, we think
                    the comparison genuinely stacks up in its favour — and below is exactly why.
                </Typography>

                <Typography component="h2" sx={heliSx.sectionHeading}>
                    At a glance
                </Typography>
                <Box component="table" sx={heliSx.table}>
                    <Box component="thead">
                        <Box component="tr">
                            <Box component="th" sx={heliSx.th} />
                            <Box component="th" sx={heliSx.th}>
                                Helicopter
                            </Box>
                            <Box component="th" sx={[heliSx.th, heliSx.thFixed]}>
                                Fixed-wing scenic flight
                            </Box>
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {comparisonRows.map((row) => (
                            <Box component="tr" key={row.aspect}>
                                <Box component="td" sx={[heliSx.td, heliSx.tdAspect]}>
                                    {row.aspect}
                                </Box>
                                <Box component="td" sx={heliSx.td}>
                                    {row.heli}
                                </Box>
                                <Box component="td" sx={[heliSx.td, heliSx.tdFixed]}>
                                    {row.fixed}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Typography component="h2" sx={heliSx.sectionHeading}>
                    The detail
                </Typography>
                {points.map((p) => (
                    <Box key={p.heading}>
                        <Typography component="h3" sx={heliSx.pointHeading}>
                            {p.heading}
                        </Typography>
                        <Typography sx={heliSx.pointBody}>{p.body}</Typography>
                    </Box>
                ))}

                <Typography component="h2" sx={heliSx.sectionHeading}>
                    So which should you choose?
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2.5, sm: 3 }}>
                    <Box sx={heliSx.chooseCard}>
                        <Typography component="h3" sx={heliSx.chooseHeading}>
                            Go by helicopter if…
                        </Typography>
                        {heliChoose.map((item) => (
                            <Box key={item} sx={heliSx.chooseItem}>
                                <CheckCircleRoundedIcon sx={{ fontSize: 19, color: 'text.disabled', mt: '1px' }} />
                                <span>{item}</span>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={[heliSx.chooseCard, heliSx.chooseCardFixed]}>
                        <Typography component="h3" sx={heliSx.chooseHeading}>
                            Go fixed-wing if…
                        </Typography>
                        {fixedChoose.map((item) => (
                            <Box key={item} sx={heliSx.chooseItem}>
                                <CheckCircleRoundedIcon sx={{ fontSize: 19, color: 'primary.main', mt: '1px' }} />
                                <span>{item}</span>
                            </Box>
                        ))}
                    </Box>
                </Stack>

                <Box sx={heliSx.ctaWrap}>
                    <Button component={Link} to="/cairns" variant="contained" color="primary" sx={heliSx.ctaButton}>
                        See our Cairns scenic flights
                    </Button>
                </Box>
            </Container>
        </>
    );
}
